import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ViewUserDetailsModal = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  const isInstitute = user?.role === 'institute';
  const isStudent = user?.role === 'student';
  const profile = isInstitute ? user?.institute_profiles?.[0] : user?.student_profiles?.[0];

  // Debug log to help identify issues
  console.log('ViewUserDetailsModal - User data:', { user, profile, isInstitute, isStudent });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success/10 text-success',
      pending: 'bg-warning/10 text-warning',
      suspended: 'bg-error/10 text-error'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles?.[status]}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      student: 'bg-primary/10 text-primary',
      institute: 'bg-secondary/10 text-secondary',
      admin: 'bg-accent/10 text-accent'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles?.[role]}`}>
        {role?.charAt(0)?.toUpperCase() + role?.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="User" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">User Details</h2>
              <p className="text-sm text-muted-foreground">Complete profile information</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="User" size={20} color="var(--color-primary)" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Full Name</p>
                <p className="font-medium text-foreground">{user?.name || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Email Address</p>
                <p className="font-medium text-foreground break-all">{user?.email || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Role</p>
                <div>{getRoleBadge(user?.role)}</div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Status</p>
                <div>{getStatusBadge(user?.status)}</div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">User ID</p>
                <p className="font-mono text-xs text-foreground break-all">{user?.id || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Registration Date</p>
                <p className="font-medium text-foreground">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Institute Profile */}
          {isInstitute && profile && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building2" size={20} color="var(--color-secondary)" />
                Institute Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Institute Name</p>
                  <p className="font-medium text-foreground">{profile?.name || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Type</p>
                  <p className="font-medium text-foreground">{profile?.type || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Location</p>
                  <p className="font-medium text-foreground">{profile?.location || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Phone</p>
                  <p className="font-medium text-foreground">{profile?.phone || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Website</p>
                  <p className="font-medium text-foreground break-all">{profile?.website || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Description</p>
                  <p className="font-medium text-foreground">{profile?.description || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Established Year</p>
                  <p className="font-medium text-foreground">{profile?.established_year || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Accreditation</p>
                  <p className="font-medium text-foreground">{profile?.accreditation || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Student Profile */}
          {isStudent && profile && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="GraduationCap" size={20} color="var(--color-primary)" />
                Student Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Phone Number</p>
                  <p className="font-medium text-foreground">{profile?.phone || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Date of Birth</p>
                  <p className="font-medium text-foreground">{formatDate(profile?.date_of_birth)}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Education Level</p>
                  <p className="font-medium text-foreground">{profile?.education_level || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Field of Interest</p>
                  <p className="font-medium text-foreground">{profile?.field_of_interest || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Address</p>
                  <p className="font-medium text-foreground">{profile?.address || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">City</p>
                  <p className="font-medium text-foreground">{profile?.city || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">State</p>
                  <p className="font-medium text-foreground">{profile?.state || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Country</p>
                  <p className="font-medium text-foreground">{profile?.country || 'N/A'}</p>
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Postal Code</p>
                  <p className="font-medium text-foreground">{profile?.postal_code || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Account Activity */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Activity" size={20} color="var(--color-success)" />
              Account Activity
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Created At</p>
                <p className="font-medium text-foreground">{formatDate(user?.created_at)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Last Updated</p>
                <p className="font-medium text-foreground">{formatDate(user?.updated_at)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Email Verified</p>
                <div className="flex items-center gap-2">
                  <Icon 
                    name={user?.email_verified ? "CheckCircle" : "XCircle"} 
                    size={16} 
                    color={user?.email_verified ? "var(--color-success)" : "var(--color-error)"}
                  />
                  <p className="font-medium text-foreground">
                    {user?.email_verified ? 'Verified' : 'Not Verified'}
                  </p>
                </div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Phone Verified</p>
                <div className="flex items-center gap-2">
                  <Icon 
                    name={user?.phone_verified ? "CheckCircle" : "XCircle"} 
                    size={16} 
                    color={user?.phone_verified ? "var(--color-success)" : "var(--color-error)"}
                  />
                  <p className="font-medium text-foreground">
                    {user?.phone_verified ? 'Verified' : 'Not Verified'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewUserDetailsModal;